/**
 * Profile-based theme tokens.
 *
 * 2026-05-12 — UX feedback Elvio: cuando se selecciona un perfil (Adulto/
 * Niño/Embarazo/Lactancia), el tono visual de la pantalla cambia
 * coherentemente. Cambiamos `--color-brand`, `--color-brand-soft`,
 * `--color-brand-ink`, `--color-bubble-me`, `--color-bubble-me-ink`,
 * `--color-bg`, `--color-bg-chat` en `document.documentElement` cuando
 * cambia el perfil. El resto del UI (chips active, bubbles "me",
 * product card pill counter) consume estos tokens y se tinea automáticamente.
 *
 * Hues OKLCH (consistente con index.css):
 *   Adulto     → 35  (coral — default brand)
 *   Niño *     → 75  (amarillo cálido / amber pediátrico)
 *   Embarazo   → 305 (lila / violeta suave)
 *   Lactancia  → 340 (rosa pálido / pink soft)
 *
 * Lightness/chroma iguales en los cuatro tonos: marca 56,5% (texto blanco ≥4,6:1 incluso en el
 * ámbar de niños) y texto suave 54% (≥4,6:1), medidos el 20-oct.
 */
import type { ProfileType } from './types/profile'

type Tokens = Record<string, string>

const DEFAULT_HUE = 35 // coral adulto

function tokensForHue(h: number): Tokens {
  return {
    '--color-bg': `oklch(98% 0.008 ${h})`,
    '--color-bg-chat': `oklch(97% 0.01 ${h})`,
    '--color-surface-2': `oklch(97% 0.01 ${h})`,
    '--color-border': `oklch(90% 0.015 ${h})`,
    '--color-border-strong': `oklch(82% 0.02 ${h})`,
    '--color-text': `oklch(22% 0.02 ${h})`,
    '--color-text-muted': `oklch(48% 0.02 ${h})`,
    '--color-text-soft': `oklch(54% 0.015 ${h})`,
    '--color-brand': `oklch(56.5% 0.16 ${h})`,
    '--color-brand-soft': `oklch(93% 0.05 ${h})`,
    '--color-brand-ink': `oklch(32% 0.1 ${h})`,
    '--color-bubble-staff-border': `oklch(92% 0.015 ${h})`,
    '--color-bubble-me': `oklch(90% 0.07 ${h})`,
    '--color-bubble-me-ink': `oklch(28% 0.1 ${h})`,
  }
}

const PROFILE_HUE: Record<ProfileType | 'default', number> = {
  default: DEFAULT_HUE, // sin perfil seleccionado
  adult: 35,            // coral
  child_3y: 75,         // amber / amarillo cálido
  child_5y: 75,
  child_8y: 75,
  child_12y: 75,
  pregnancy: 305,       // lila / violeta
  lactation: 340,       // rosa suave
}

export function applyProfileTheme(profile: ProfileType | null): void {
  const hue = profile === null ? PROFILE_HUE.default : PROFILE_HUE[profile]
  const tokens = tokensForHue(hue)
  const root = document.documentElement
  for (const [k, v] of Object.entries(tokens)) {
    root.style.setProperty(k, v)
  }
}

/** Label humano del perfil — usado por Composer "Para: <label>". */
export function profileLabel(profile: ProfileType | null): string {
  switch (profile) {
    case 'adult':
      return 'Adulto'
    case 'child_3y':
      return 'Niño 0–4 años'
    case 'child_5y':
      return 'Niño 5–7 años'
    case 'child_8y':
      return 'Niño 8–11 años'
    case 'child_12y':
      return 'Adolescente 12+ años'
    case 'pregnancy':
      return 'Embarazada'
    case 'lactation':
      return 'Lactando'
    default:
      return ''
  }
}
